import React from 'react';
import './home.css';
import { Link } from 'react-router-dom';
import { getValidStoredUser } from '../config/session';
import logo from '../images/QuiblyLight.png';
import heroImage from '../images/heroLanding.png';
import PricingCards from '../pages/Pricing/pricingCards';
import { templates } from '../pages/ResumeBuilder/templates/index';
import { FiArrowRight, FiZap, FiFileText, FiEdit3, FiDownload } from 'react-icons/fi';

const FEATURES = [
  {
    icon: <FiZap size={20} />,
    title: 'Tailored by AI',
    text: 'Paste a LinkedIn job posting and our AI reshapes your real experience to mirror the role\u2019s keywords and requirements \u2014 truthfully, never invented.',
    tint: 'green'
  },
  {
    icon: <FiFileText size={20} />,
    title: 'Real LaTeX templates',
    text: 'Professionally typeset LaTeX templates — the same quality you\u2019d get from Overleaf, with zero LaTeX knowledge required.',
    tint: 'pink'
  },
  {
    icon: <FiEdit3 size={20} />,
    title: 'Overleaf-style editor',
    text: 'Fine-tune anything in a split-pane editor: LaTeX source on the left, live compiled PDF preview on the right.',
    tint: 'blue'
  },
  {
    icon: <FiDownload size={20} />,
    title: 'Download instantly',
    text: 'Compile and download a pixel-perfect PDF on any plan. Save your tailored resumes to your library on paid tiers.',
    tint: 'amber'
  }
];

function Home() {
  // Skip login when the user still has a valid (non-expired) session.
  const startDestination = getValidStoredUser() ? '/app/resume' : '/register';

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing">
      {/* ===== Hero ===== */}
      <section className="landingHero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="heroCenter">
          <span className="heroBadge">AI-powered resume tailoring</span>
          <h1 className="heroTitle">Land the job.<br />Effortlessly.</h1>
          <p className="heroSubline">
            Pick a LaTeX template, paste a LinkedIn job posting, and get a
            beautifully typeset resume tailored to the role.
          </p>
          <div className="heroCtas">
            <Link to={startDestination} className="heroCta primary">
              Get Started <FiArrowRight size={15} />
            </Link>
            <button className="heroCta ghost" onClick={() => scrollTo('templates')}>
              See Templates
            </button>
          </div>
        </div>

        <button className="scrollPill" onClick={() => scrollTo('features')}>
          SCROLL &#8595;
        </button>
      </section>

      {/* ===== Features ===== */}
      <section className="landingSection" id="features">
        <span className="sectionKicker">Why Quibly</span>
        <h2 className="sectionTitle">Your experience, tailored to every job</h2>
        <p className="sectionSubtitle">
          Stop rewriting your resume for every application. Build your profile once and
          let AI do the tailoring.
        </p>
        <div className="featureGrid">
          {FEATURES.map((f) => (
            <div className={`featureCard tint-${f.tint}`} key={f.title}>
              <div className="featureIcon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Templates ===== */}
      <section className="landingSection alt" id="templates">
        <span className="sectionKicker">Templates</span>
        <h2 className="sectionTitle">Professionally typeset in LaTeX</h2>
        <p className="sectionSubtitle">
          Twenty designs, from classic to creative. Every one compiles to a crisp, ATS-friendly PDF.
        </p>
        <div className="templateStrip">
          {templates.map((t) => (
            <div className="templateStripCard" key={t.id}>
              <img src={t.preview} alt={t.name} loading="lazy" />
              <div className="templateStripMeta">
                <span className="templateStripName">{t.name}</span>
                <span className="templateStripTag">{t.tag}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section className="landingSection" id="pricing">
        <span className="sectionKicker">Pricing</span>
        <h2 className="sectionTitle">Start free, upgrade when you need more</h2>
        <p className="sectionSubtitle">
          Two free tailored resumes every day. No credit card required.
        </p>
        <PricingCards />
      </section>

      {/* ===== Final CTA ===== */}
      <section className="landingFinalCta">
        <h2>Ready to build your next resume?</h2>
        <Link to={startDestination} className="heroCta primary dark">
          Start Building for Free <FiArrowRight size={15} />
        </Link>
      </section>

      {/* ===== Footer ===== */}
      <footer className="landingFooter">
        <div className="footerInner">
          <img src={logo} alt="Quibly" className="footerLogo" />
          <ul className="footerNav">
            <li><a href="mailto:quiblycontact@gmail.com">quiblycontact@gmail.com</a></li>
            <li><Link to="/privacypolicy">Privacy Policy</Link></li>
            <li><Link to="/termsandconditions">Terms and Conditions</Link></li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

export default Home;
